import React, { useState } from 'react';
import { Employee } from '@tymbe/legislatives/Employee';
import { Employer } from '@tymbe/legislatives/Employer';
import { WorkShift } from '@tymbe/legislatives/WorkShift';
import { PayPeriod } from '@tymbe/legislatives/PayPeriod';
import { PayPeriodInterval } from '@tymbe/legislatives/PayPeriodInterval';
import { CzechContract } from '@tymbe/legislatives/czechia/CzechContract';
import { CzechContractType } from '@tymbe/legislatives/czechia/CzechContractType';
import { CzechDocumentType } from '@tymbe/legislatives/czechia/CzechDocumentType';
import { CzechTestProviders } from '@tymbe/legislatives/czechia/CzechTestProviders';
import { CzechDocument } from '@tymbe/legislatives/czechia/CzechDocument';
import moment from 'moment-timezone';
import { PaySupplementType } from '@tymbe/legislatives/PaySupplementType';
import { PaySupplement } from '@tymbe/legislatives/PaySupplement';
import { CzechWorkCalculator } from '@tymbe/legislatives/czechia/CzechWorkCalculator';
import { timezone } from './timezone';
import { WithKey } from './WithKey';
import { PaySupplementAmountType } from './PaySupplementAmountType';
import { CalculationResult } from './CalculationResult';
import { PaySupplementValueType } from './PaySupplementValueType';
import { WorkShiftStatus } from '@tymbe/legislatives/WorkShiftStatus';
import { WorkShiftType } from '@tymbe/legislatives/WorkShiftType';
import { PaySupplementForCalculator } from './PaySupplementForCalculator';
import { CzechAvailableContract } from '@tymbe/legislatives/czechia/CzechAvailableContract';
import { czechContractTypeNameMap } from './contractTypeNameMap';
import { SalaryRestrictionStatus } from '@tymbe/legislatives/SalaryRestrictionStatus';
import { salaryRestrictionStatusNameMap } from './salaryRestrictionStatusNameMap';
import { SalaryComponent } from '@tymbe/legislatives/SalaryComponent';
import { SalaryComponentUnit } from '@tymbe/legislatives/SalaryComponentUnit';
import { SalaryComponentCalculation } from '@tymbe/legislatives/SalaryComponentCalculation';
import { paySupplementTypeNameMap } from './paySupplementTypeNameMap';
import { salaryComponentUnitNameMap } from './salaryComponentUnitNameMap';
import { salaryComponentCalculationIntentionNameMap } from './salaryComponentCalculationIntentionNameMap';
import { getConst } from '@tymbe/legislatives/Constants';
import { CzechConstantType } from '@tymbe/legislatives/czechia/CzechConstantType';
import { SalaryComponentType } from '@tymbe/legislatives/SalaryComponentType';
import { SalaryAdvancePhase } from '@tymbe/legislatives/SalaryAdvancePhase';
import { documentTypeNameMap } from './documentTypeNameMap';
import { CzechSalaryType } from '@tymbe/legislatives/czechia/CzechSalaryType';
import { CzechSalaryComponent } from '@tymbe/legislatives/czechia/CzechSalaryComponent';
import { salaryTypeNameMap } from './salaryTypeNameMap';
import { salaryAdvancePhaseNameMap } from './salaryAdvancePhaseNameMap';
import { workShiftTypeNameMap } from './workShiftTypeNameMap';

const priceFormat = new Intl.NumberFormat('cs-CZ', { style: 'currency', currency: 'CZK', minimumFractionDigits: 0 });
const nbsp = "\u00A0";

export function newMoment(s?: string | number[]): moment.Moment {
  return moment.tz(s, timezone);
}

interface ContractEntityGroup {
  contract: CzechContract;
  workShifts: WithKey<WorkShift>[];
}

interface ExportedData {
  payIntervalYear: number;
  payIntervalMonth: number;
  employeeDateOfBirth: string;
  allowBypassSalaryLimit: boolean;
  contractGroups: Record<string, ContractEntityGroup>;
  paySupplementRules: PaySupplementForCalculator[];
  salaryAdvanceRestrictionStatus: SalaryRestrictionStatus | null;
  documents: WithKey<CzechDocument>[];
  salaryAdvances: Record<CzechSalaryType, Record<SalaryAdvancePhase, number>>;
  externalComponents: WithKey<CzechSalaryComponent<"external">>[];
}

function createEmptyContractGroup(id: string, employee: Employee, employer: Employer, now: moment.Moment): ContractEntityGroup {
  return {
    contract: {
      type: CzechContractType.DohodaOProvedeniPrace,
      expectedWeeklyHours: undefined,
      iscoPrefixes: [''],
      id,
      templateId: id,
      employeeId: employee.id,
      employerId: employer.id,
      startsOn: now.clone().startOf('year').format(),
      signedOn: now.clone().subtract(1, 'year').endOf('year').format(),
      expiresOn: now.clone().endOf('year').format(),
      endsOn: now.clone().endOf('year').format(),
      fixedValues: {},
    },
    workShifts: [],
  };
}

function editByKey<T>(editKey: string, fn: (item: T) => T) {
  return (item: WithKey<T>): WithKey<T> => item.key === editKey ? { ...item, value: fn(item.value) } : item;
}

export default function MonthlySalaryCalculator() {
  const now = newMoment();
  const [salaryAdvanceRestrictionStatus, setSalaryAdvanceRestrictionStatus] = useState<SalaryRestrictionStatus | null>(null);
  const [payIntervalYear, setPayIntervalYear] = useState(now.year());
  const [payIntervalMonth, setPayIntervalMonth] = useState(now.month() + 1);
  const payInterval: PayPeriodInterval = {
    period: PayPeriod.Monthly,
    startsOn: newMoment([payIntervalYear, payIntervalMonth - 1, 1]).format(),
  };
  const [employeeDateOfBirth, _setEmployeeDateOfBirth] = useState('2000-03-04');
  const employee: Employee = {
    id: 'employee',
    dateOfBirth: newMoment(employeeDateOfBirth).format(),
  };
  const employer: Employer = {
    id: 'employer',
  };
  const initialContractId = crypto.randomUUID();
  const [contractGroups, setContractGroups] = useState<Record<string, ContractEntityGroup>>({
    [initialContractId]: createEmptyContractGroup(initialContractId, employee, employer, now),
  });
  const [externalComponents, setExternalComponents] = useState<WithKey<CzechSalaryComponent<"external">>[]>([]);
  const [documents, setDocuments] = useState<WithKey<CzechDocument>[]>([]);
  const [allowBypassSalaryLimit, setAllowBypassSalaryLimit] = useState<boolean>(false);
  const [salaryAdvances, setSalaryAdvances] = useState<Record<CzechSalaryType, Record<SalaryAdvancePhase, number>>>({
    [CzechSalaryType.DPP]: {
      [SalaryAdvancePhase.Authorized]: 0,
      [SalaryAdvancePhase.Paid]: 0,
    },
    [CzechSalaryType.DPC_HPP]: {
      [SalaryAdvancePhase.Authorized]: 0,
      [SalaryAdvancePhase.Paid]: 0,
    },
  });

  const [forceUpdateState, setForceUpdateState] = useState<boolean>(false);
  const [paySupplementRules, setPaySupplementRules] = useState<PaySupplementForCalculator[]>([
    {
      amountType: PaySupplementAmountType.Percentage,
      appliesToEntireShift: true,
      description: '',
      enabled: false,
      fixedHourlyRate: 0,
      percentageIncrease: 100,
      type: PaySupplementType.Holiday,
    },
    {
      amountType: PaySupplementAmountType.Percentage,
      appliesToEntireShift: true,
      description: '',
      enabled: false,
      fixedHourlyRate: 0,
      percentageIncrease: 20,
      type: PaySupplementType.Night,
    },
    {
      amountType: PaySupplementAmountType.Percentage,
      appliesToEntireShift: true,
      description: '',
      enabled: false,
      fixedHourlyRate: 0,
      percentageIncrease: 25,
      type: PaySupplementType.Saturday,
    },
    {
      amountType: PaySupplementAmountType.Percentage,
      appliesToEntireShift: true,
      description: '',
      enabled: false,
      fixedHourlyRate: 0,
      percentageIncrease: 25,
      type: PaySupplementType.Sunday,
    },
  ]);

  const [result, setResult] = useState<CalculationResult>({
    errorMessages: [],
    salaries: [],
    workShifts: [],
  });

  function forceUpdate(): void {
    setForceUpdateState(!forceUpdateState);
  }

  function addExternalComponent(): void {
    const componentId = crypto.randomUUID();
    setExternalComponents([
      {
        key: componentId,
        value: {
          salaryType: CzechSalaryType.DPP,
          amount: 0,
          calculation: null,
          description: "",
          name: `Externí složka #${componentId.slice(0, 4)}`,
          unit: SalaryComponentUnit.CZK,
          isEmployerCost: false,
        }
      },
      ...externalComponents,
    ]);
  }

  function setExternalComponentSalaryType(key: string, salaryType: CzechSalaryType): void {
    setExternalComponents(externalComponents.map(editByKey(key, (value) => ({
      ...value,
      salaryType,
    }))));
  }

  function setExternalComponentUnit(key: string, unit: SalaryComponentUnit): void {
    setExternalComponents(externalComponents.map(editByKey(key, (value) => ({
      ...value,
      unit,
    }))));
  }

  function setExternalComponentCalculation(key: string, calculation: SalaryComponentCalculation): void {
    setExternalComponents(externalComponents.map(editByKey(key, (value) => ({
      ...value,
      calculation,
    }))));
  }

  function setExternalComponentName(key: string, name: string): void {
    setExternalComponents(externalComponents.map(editByKey(key, (value) => ({
      ...value,
      name,
    }))));
  }

  function setExternalComponentAmount(key: string, amount: number): void {
    setExternalComponents(externalComponents.map(editByKey(key, (value) => ({
      ...value,
      amount,
    }))));
  }

  function setExternalComponentIsEmployerCost(key: string, isEmployerCost: boolean): void {
    setExternalComponents(externalComponents.map(editByKey(key, (value) => ({
      ...value,
      isEmployerCost,
    }))));
  }

  function removeExternalComponent(keyToRemove: string): void {
    setExternalComponents(externalComponents.filter(({ key }) => key !== keyToRemove));
  }

  function addDocument(): void {
    const documentId = crypto.randomUUID();
    setDocuments([
      {
        key: documentId,
        value: {
          id: documentId,
          employeeId: employee.id,
          startsOn: now.clone().startOf('year').format(),
          expiresOn: now.clone().endOf('year').format(),
          type: CzechDocumentType.ProhlaseniPoplatnika,
        },
      },
      ...documents,
    ]);
  }

  function setDocumentType(key: string, type: CzechDocumentType): void {
    setDocuments(documents.map(editByKey(key, (value) => ({
      ...value,
      type,
    }))));
  }

  function setDocumentStartsOn(key: string, dateString: string): void {
    const date = newMoment(dateString);
    if (!date.isValid()) {
      return;
    }
    setDocuments(documents.map(editByKey(key, (value) => ({
      ...value,
        startsOn: date.format()
    }))));
  }

  function setDocumentExpiresOn(key: string, dateString: string): void {
    const date = newMoment(dateString);
    if (!date.isValid()) {
      return;
    }
    setDocuments(documents.map(editByKey(key, (value) => ({
      ...value,
      expiresOn: date.format()
    }))));
  }

  function removeDocument(keyToRemove: string): void {
    setDocuments(documents.filter(({ key }) => key !== keyToRemove));
  }

  function setSalaryAdvance(salaryType: CzechSalaryType, phase: SalaryAdvancePhase, advance: number): void {
    setSalaryAdvances({
      ...salaryAdvances,
      [salaryType]: {
        ...salaryAdvances[salaryType],
        [phase]: advance,
      },
    });
  }

  function addWorkShift(groupKey: string): void {
    const contractGroup = contractGroups[groupKey];
    if (!contractGroup) {
      return;
    }

    const contractStartsOn = moment.tz(contractGroup.contract.startsOn, timezone);
    const defaultStartTime = contractStartsOn.clone().add(1, 'day').year(payIntervalYear).month(payIntervalMonth - 1).hours(8).minutes(0).seconds(0);
    const id = crypto.randomUUID();
    setContractGroups({
      ...contractGroups,
      [groupKey]: {
        ...contractGroup,
        workShifts: [
          ...contractGroup.workShifts,
          {
            key: id,
            value: {
              contractId: contractGroup.contract.id,
              employeeId: employee.id,
              employerId: employer.id,
              finishTime: defaultStartTime.clone().add(1, 'hours').format(),
              hourlyRate: 150,
              iscoCode: '',
              offerId: id,
              paySupplements: [],
              startTime: defaultStartTime.format(),
              status: WorkShiftStatus.Worked,
              type: WorkShiftType.Regular,
              workBreaks: [],
              workId: id,
            },
          }
        ]
      },
    });
  }

  function removeWorkShift(groupKey: string, workShiftKey: string): void {
    const contractGroup = contractGroups[groupKey];
    if (!contractGroup) {
      return;
    }

    setContractGroups({
      ...contractGroups,
      [groupKey]: {
        ...contractGroup,
        workShifts: contractGroup.workShifts.filter(({ key }) => key !== workShiftKey),
      },
    });
  }

  function setWorkShiftType(groupKey: string, workShiftKey: string, type: WorkShiftType): void {
    const contractGroup = contractGroups[groupKey];
    if (!contractGroup) {
      return;
    }
    setContractGroups({
      ...contractGroups,
      [groupKey]: {
        ...contractGroup,
        workShifts: contractGroup.workShifts.map(editByKey(workShiftKey, (value) => ({
          ...value,
          type,
        }))),
      },
    });
  }

  function setWorkShiftStartTime(groupKey: string, workShiftKey: string, dateTimeString: string): void {
    const contractGroup = contractGroups[groupKey];
    if (!contractGroup) {
      return;
    }
    const dateTime = newMoment(dateTimeString);
    if (!dateTime.isValid()) {
      return;
    }
    setContractGroups({
      ...contractGroups,
      [groupKey]: {
        ...contractGroup,
        workShifts: contractGroup.workShifts.map(editByKey(workShiftKey, (value) => ({
          ...value,
          startTime: dateTime.format(),
        }))),
      },
    });
  }

  function setWorkShiftFinishTime(groupKey: string, workShiftKey: string, dateTimeString: string): void {
    const contractGroup = contractGroups[groupKey];
    if (!contractGroup) {
      return;
    }
    const dateTime = newMoment(dateTimeString);
    if (!dateTime.isValid()) {
      return;
    }
    setContractGroups({
      ...contractGroups,
      [groupKey]: {
        ...contractGroup,
        workShifts: contractGroup.workShifts.map(editByKey(workShiftKey, (value) => ({
          ...value,
          finishTime: dateTime.format(),
        }))),
      },
    });
  }

  function setWorkShiftHourlyRate(groupKey: string, workShiftKey: string, hourlyRate: number): void {
    const contractGroup = contractGroups[groupKey];
    if (!contractGroup) {
      return;
    }
    if (isNaN(hourlyRate)) {
      hourlyRate = 0;
    }
    if (hourlyRate < 0) {
      return;
    }
    setContractGroups({
      ...contractGroups,
      [groupKey]: {
        ...contractGroup,
        workShifts: contractGroup.workShifts.map(editByKey(workShiftKey, (value) => ({
          ...value,
          hourlyRate,
        }))),
      },
    });
  }

  function setWorkShiftIncludeBreak(groupKey: string, workShiftKey: string, enabled: boolean): void {
    const contractGroup = contractGroups[groupKey];
    if (!contractGroup) {
      return;
    }
    setContractGroups({
      ...contractGroups,
      [groupKey]: {
        ...contractGroup,
        workShifts: contractGroup.workShifts.map(editByKey(workShiftKey, (value) => {
          if (enabled === value.workBreaks.length >= 1) {
            return value;
          }

          return {
            ...value,
            workBreaks: enabled ? [{
              startsAt: newMoment(value.startTime).add(1, 'hour').format(),
              durationInMinutes: 30,
              endsAt: newMoment(value.startTime).add(1.5, 'hours').format(),
            }] : [],
          };
        }))
      },
    });
  }

  function setWorkShiftBreakStart(groupKey: string, workShiftKey: string, dateTimeString: string): void {
    const contractGroup = contractGroups[groupKey];
    if (!contractGroup) {
      return;
    }
    const start = newMoment(dateTimeString);
    if (!start.isValid()) {
      return;
    }
    setContractGroups({
      ...contractGroups,
      [groupKey]: {
        ...contractGroup,
        workShifts: contractGroup.workShifts.map(editByKey(workShiftKey, (value) => {
          const end = start.clone().add(value.workBreaks[0].durationInMinutes, 'minutes');
          const [firstBreak] = value.workBreaks;
          return {
            ...value,
            workBreaks: [
              { ...firstBreak, startsAt: start.format(), endsAt: end.format() },
            ]
          };
        })),
      },
    });
  }

  function setWorkShiftBreakDuration(groupKey: string, workShiftKey: string, duration: number): void {
    const contractGroup = contractGroups[groupKey];
    if (!contractGroup) {
      return;
    }
    duration = isNaN(duration) ? 0 : duration;
    if (duration < 0) {
      return;
    }
    setContractGroups({
      ...contractGroups,
      [groupKey]: {
        ...contractGroup,
        workShifts: contractGroup.workShifts.map(editByKey(workShiftKey, (value) => {
          const [firstBreak] = value.workBreaks;
          return {
            ...value,
            workBreaks: [
              {
                ...firstBreak,
                durationInMinutes: duration,
                endsAt: newMoment(value.workBreaks[0].startsAt).add(duration, 'minutes').format(),
              },
            ],
          };
        })),
      },
    });
  }

  function setEmployeeDateOfBirth(dateString: string): void {
    if (newMoment(dateString).isValid()) {
      employee.dateOfBirth = newMoment(dateString).format();
      _setEmployeeDateOfBirth(dateString);
    }
  }

  function setContractDateProperty(groupKey: string, propertyName: 'signedOn' | 'startsOn' | 'expiresOn', dateString: string): void {
    const date = newMoment(dateString);
    if (date.isValid()) {
      contractGroups[groupKey].contract[propertyName] = date.format();
      if (propertyName === "expiresOn") {
        contractGroups[groupKey].contract.endsOn = date.format();
      }
      forceUpdate();
    }
  }

  function setContractType(groupKey: string, contractType: CzechContractType): void {
    const contractGroup = contractGroups[groupKey];
    if (!contractGroup) {
      return;
    }
    setContractGroups({
      ...contractGroups,
      [groupKey]: {
        ...contractGroup,
        contract: {
          ...contractGroup.contract,
          type: contractType,
        }
      },
    });
    forceUpdate();
  }

  function setPaySupplementRuleProperty(
    type: PaySupplementType,
    property: keyof PaySupplementForCalculator,
    value: PaySupplementValueType,
  ): void {
    const paySupplement = paySupplementRules.find((rule: PaySupplementForCalculator) => rule.type === type);
    if (paySupplement) {
      if (property === 'amountType') {
        const propertyName = value === PaySupplementAmountType.Percentage ? 'percentageIncrease' : 'fixedHourlyRate';
        paySupplement[propertyName] = 0;
      }
      (paySupplement[property] as PaySupplementValueType) = value;
    }
    setPaySupplementRules(paySupplementRules);
    forceUpdate();
  }

  function getIntervalForWorkShift(workShift: WorkShift): PayPeriodInterval {
    const workShiftStartTime = newMoment(workShift.startTime);
    const startDate = newMoment([workShiftStartTime.year(), workShiftStartTime.month(), 1]);
    return {
      period: PayPeriod.Monthly,
      startsOn: startDate.format(),
    };
  }

  async function recalculate(): Promise<void> {
    const newResult: CalculationResult = {
      errorMessages: [],
      salaries: [],
      workShifts: [],
    };
    const providers = new CzechTestProviders();
    const calculator = new CzechWorkCalculator(
      providers.employee,
      providers.employer,
      providers.salaryAdvance,
      providers.salaryComponent,
      providers.restriction,
      providers.workShift,
      providers.document,
      providers.contract,
    );

    providers.addEmployee(employee);
    providers.addEmployer(employer);
    providers.addDocumentsSmart(documents.map(document => document.value));

    for (const salaryType of Object.values(CzechSalaryType)) {
      for (const phase of Object.values(SalaryAdvancePhase)) {
        const amount = salaryAdvances[salaryType][phase];
        if (amount) {
          providers.salaryAdvance.add({
            employeeId: employee.id,
            employerId: employer.id,
            paidOn: newMoment([payIntervalYear, payIntervalMonth - 1, 7]).format(),
            amount,
            type: salaryType,
            phase,
          });
        }
      }
    }

    for (const externalComponent of externalComponents) {
      providers.salaryComponent.add(externalComponent.key, {
        ...externalComponent.value,
        employeeId: employee.id,
        employerId: employer.id,
        monthStart: payInterval.startsOn,
      });
    }

    if (salaryAdvanceRestrictionStatus) {
      providers.restriction.add({
        employeeId: employee.id,
        employerId: employer.id,
        status: salaryAdvanceRestrictionStatus,
        validFrom: payInterval.startsOn,
      });
    }

    for (const contractGroup of Object.values(contractGroups)) {
      const { contract } = contractGroup;
      providers.addContract(contract);
    }

    const paySupplementRulesForCalculator = getPaySupplementRulesForCalculator(paySupplementRules);
    for (const { contract, workShifts } of Object.values(contractGroups)) {
      const availableContract: CzechAvailableContract = {
        computeExpiresOn(_startsOn) {
          return contract.endsOn;
        },
        iscoPrefixes: [''],
        fixedContractId: contract.id,
        fixedValues: {},
        startsOn: contract.startsOn,
        templateId: contract.templateId,
        type: contract.type,
      };

      for (const { value: workShift } of workShifts) {
        try {
          if (getIntervalForWorkShift(workShift).startsOn !== payInterval.startsOn) {
              continue;
          }
          CzechWorkCalculator.addRequiredBreakToWorkShift(workShift, employee);
          CzechWorkCalculator.addPaySupplementsToWorkShift(workShift, paySupplementRulesForCalculator);

          const signUp = await calculator.canEmployeeSignUpForWorkShift(employee.id, workShift, [availableContract], allowBypassSalaryLimit, allowBypassSalaryLimit, true);
          providers.workShift.add(workShift);
          const salary = CzechWorkCalculator.calculateForWorkShift(workShift);
          newResult.workShifts.push({
            workShift,
            signUp,
            salary,
          });
        } catch (error) {
          newResult.errorMessages.push(String(error));
        }
      }
    }

    try {
      const salaries = await calculator.calculateSalaries(
        employee.id,
        employer.id,
        payInterval,
        allowBypassSalaryLimit,
        allowBypassSalaryLimit,
      );
      newResult.salaries.push(...salaries);
      newResult.errorMessages.push(...salaries.flatMap(({ errors }) => errors).map(String));
    } catch (error) {
      newResult.errorMessages.push(String(error));
    }

    setResult(newResult);
  }

  function getExportString(): string {
    return JSON.stringify({
      payIntervalYear,
      payIntervalMonth,
      employeeDateOfBirth,
      allowBypassSalaryLimit,
      contractGroups,
      paySupplementRules,
      salaryAdvanceRestrictionStatus,
      salaryAdvances,
      documents,
      externalComponents,
    } satisfies ExportedData);
  }

  async function copyExport(): Promise<void> {
    const exportTextarea = document.getElementById('exportTextarea') as HTMLInputElement;
    if (!exportTextarea) {
      return;
    }
    exportTextarea.focus();
    exportTextarea.setSelectionRange(0, 9999);
    await navigator.clipboard.writeText(getExportString());
  }

  async function pasteImport(): Promise<void> {
    const importTextarea = document.getElementById('importTextarea') as HTMLInputElement;
    if (!importTextarea) {
      return;
    }
    try {
      importTextarea.value = await navigator.clipboard.readText();
    } catch (err) {
      console.error(err);
    }
  }

  function submitImport(): void {
    const importTextarea = document.getElementById('importTextarea') as HTMLInputElement;
    if (!importTextarea) {
      return;
    }
    if (!importTextarea.value) {
      alert('Neplatný datový řetězec');
      return;
    }
    try {
      importString(importTextarea.value);
      importTextarea.value = '';
    } catch {
      alert('Neplatný datový řetězec');
    }
  }

  function importString(str: string): void {
    const importData = JSON.parse(str) as ExportedData;
    setPayIntervalYear(importData.payIntervalYear);
    setPayIntervalMonth(importData.payIntervalMonth);
    _setEmployeeDateOfBirth(importData.employeeDateOfBirth);
    setAllowBypassSalaryLimit(importData.allowBypassSalaryLimit);
    employee.dateOfBirth = importData.employeeDateOfBirth;
    setSalaryAdvanceRestrictionStatus(importData.salaryAdvanceRestrictionStatus);
    setDocuments(documents);
    setSalaryAdvances(importData.salaryAdvances);
    setExternalComponents(importData.externalComponents);
    setContractGroups(importData.contractGroups);
    setPaySupplementRules(importData.paySupplementRules);
    forceUpdate();
  }

  function displayMinutes(minutes: number): string {
    const displayedHours = Math.floor(minutes / 60);
    const displayedMinutes = minutes % 60;
    return `${displayedHours ? displayedHours + 'h': ''} ${displayedMinutes}m`;
  }

  function getPaySupplementRulesForCalculator(rules: PaySupplementForCalculator[]): PaySupplement[] {
    return rules
      .filter((rule: PaySupplementForCalculator) => rule.enabled)
      .map((rule: PaySupplementForCalculator) => {
        const { enabled: _enabled, amountType: _amountType, ...rest } = rule;
        return rest;
      });
  }

  function addContractGroup(): void {
    const key = crypto.randomUUID();
    setContractGroups({
      [key]: createEmptyContractGroup(key, employee, employer, now),
      ...contractGroups,
    })
  }

  function removeContractGroup(groupKey: string): void {
    const { [groupKey]: _contractGroup, ...otherContractGroups } = contractGroups;
    setContractGroups(otherContractGroups);
  }

  return (
    <div className="App">
      <div className="navbar navbar-expand-lg bg-body-tertiary">
        <div className="container-fluid">
          <a className="navbar-brand" href="#_">
            Tymbe - Kalkulačka měsíční mzdy
          </a>
        </div>
      </div>

      <div className="container mt-3">
        {result.errorMessages.map((errorMessage) => (
          <div className="alert alert-danger mt-3" key={errorMessage}>
            {errorMessage}
          </div>
        ))}

        <div className="row">
          <div className="col-6">
            <div className="card">
              <div className="card-header">
                <b>Nastavení výpočtu</b>
              </div>
                <div className="card-body">
                  <div className="mb-2">
                    <label className="form-label">
                      Období výpočtu
                    </label>
                    <div className="row">
                      <div className="col-6">
                        <div className="input-group col">
                          <span className="input-group-text">Rok</span>
                          <select
                            className="form-select"
                            onChange={ev => setPayIntervalYear(parseInt((ev.target as HTMLSelectElement).value))}
                            value={payIntervalYear}
                          >
                            {[...Array(10).keys()].map(x => 2020 + x).map(year => <option key={year} value={year}>{year}</option>)}
                          </select>
                        </div>
                      </div>
                      <div className="col-6 mb-2">
                        <div className="input-group">
                          <span className="input-group-text">Měsíc</span>
                          <select
                            className="form-select"
                            onChange={ev => setPayIntervalMonth(parseInt((ev.target as HTMLSelectElement).value))}
                            value={payIntervalMonth}
                            >
                              {[...Array(12).keys()].map(x => 1 + x).map(month => <option key={month} value={month}>{month}</option>)}
                          </select>
                        </div>
                      </div>
                      <div className="col-12 mb-2">
                        <div className="form-check">
                          <input
                            type="checkbox"
                            id={`czechContractAllowBypassSalaryLimit`}
                            className="form-check-input"
                            checked={allowBypassSalaryLimit}
                            onChange={ev => {
                              setAllowBypassSalaryLimit(ev.target.checked);
                              forceUpdate();
                            }}
                          />
                          <label className="form-check-label" htmlFor={`czechContractAllowBypassSalaryLimit`}>
                            Povolit překročení měsíčního limitu mzdy
                          </label>
                        </div>
                      </div>

                      {(Object.entries(salaryTypeNameMap) as [CzechSalaryType, string][]).map(([type, typeName]) => (
                        (Object.entries(salaryAdvancePhaseNameMap) as [SalaryAdvancePhase, string][]).map(([phase, phaseName]) => (
                          <div key={type + phase} className="col-6">
                            <b>{typeName}</b>
                            <div className="mb-2">
                              <label className="form-label">{phaseName} zálohy</label>
                              <div className="input-group">
                                <input
                                  type="number"
                                  className="form-control text-end"
                                  min={0}
                                  onChange={ev => setSalaryAdvance(type, phase, Number(ev.target.value))}
                                  value={salaryAdvances[type][phase]}
                                  placeholder="0"
                                />
                                <span className="input-group-text">Kč</span>
                              </div>
                            </div>
                          </div>
                        ))
                      ))}
                    </div>
                  </div>
                  <div className="mb-2">
                    <div className="mb-2">
                      <label className="form-label">Exekuce</label>
                      <div className="form-check">
                        <input
                          type="radio"
                          name="salaryAdvanceRestrictionStatus"
                          id="salaryAdvanceRestrictionStatus-none"
                          className="form-check-input"
                          checked={salaryAdvanceRestrictionStatus === null}
                          onChange={ev => {
                            if ((ev.target as HTMLInputElement).checked) {
                              setSalaryAdvanceRestrictionStatus(null);
                            }
                          }}
                        />
                        <label className="form-check-label" htmlFor="salaryAdvanceRestrictionStatus-none">
                          Žádná
                        </label>
                      </div>
                      {(Object.entries(salaryRestrictionStatusNameMap) as [SalaryRestrictionStatus, string][]).map(([status, name]) => {
                        let suffix = "";
                        if (status === SalaryRestrictionStatus.MinimalniNezabavitelnaCastka) {
                          const nezabavitelnaCastka = getConst(CzechConstantType.NEZABAVITELNA_CASTKA, payInterval.startsOn)
                          suffix = priceFormat.format(nezabavitelnaCastka);
                        }
                        return (
                          <div className="form-check" key={status}>
                            <input
                              type="radio"
                              name="salaryAdvanceRestrictionStatus"
                              id={`salaryAdvanceRestrictionStatus-${status}`}
                              className="form-check-input"
                              checked={salaryAdvanceRestrictionStatus === status}
                              onChange={ev => {
                                if ((ev.target as HTMLInputElement).checked) {
                                  setSalaryAdvanceRestrictionStatus(status);
                                }
                              }}
                            />
                            <label className="form-check-label" htmlFor={`salaryAdvanceRestrictionStatus-${status}`}>
                              {name}

                              {suffix && (
                                <span className="text-muted"> ({suffix})</span>
                              )}
                            </label>
                          </div>
                        )
                      })}
                    </div>
                  </div>
              </div>
            </div>
            <div className="card mt-3">
              <div className="card-header d-flex justify-content-between align-items-center">
                <b>Smlouvy</b>

                <button
                  onClick={addContractGroup}
                  className="btn-success btn btn-sm"
                >
                  Přidat smlouvu
                </button>
              </div>
              <div className="card-body">
                {Object.entries(contractGroups).map(([contractKey, { contract }]) => {
                    const contractStartsOn = newMoment(contract.startsOn);
                    const contractExpiresOn = newMoment(contract.expiresOn);
                    return (
                      <div key={contractKey}>
                        <div className="d-flex justify-content-between align-items-center">
                          <b>Smlouva: #{contractKey.slice(0, 4)}</b>
                          <button
                            onClick={() => removeContractGroup(contractKey)}
                            className="btn mb-2 btn-danger btn-sm"
                          >
                            Odebrat smlouvu
                          </button>
                        </div>
                        <div className="mb-2">
                          <label className="form-label">Podepsáno dne</label>
                          <input
                            type="date"
                            className="form-control"
                            value={newMoment(contract.signedOn).format("YYYY-MM-DD")}
                            onChange={ev => setContractDateProperty(contractKey, "signedOn", ev.target.value)}
                          />
                        </div>
                        <div className="row mb-2">
                          <div className="col-6">
                            <label className="form-label">Platnost od</label>
                            <input
                              type="date"
                              className={`form-control ${contractStartsOn.isAfter(contractExpiresOn)
                                ? 'is-invalid'
                                : ''}`}
                              value={contractStartsOn.format("YYYY-MM-DD")}
                              onChange={ev => setContractDateProperty(contractKey, "startsOn", ev.target.value)}
                            />
                            <div className="invalid-feedback">
                              Začátek platnosti by měl být dříve než konec!
                            </div>
                          </div>
                          <div className="col-6">
                            <label className="form-label">Platnost do</label>
                            <input
                              type="date"
                              className={`form-control ${contractStartsOn.isAfter(contractExpiresOn)
                                ? 'is-invalid'
                                : ''}`}
                              value={contractExpiresOn.format("YYYY-MM-DD")}
                              onChange={ev => setContractDateProperty(contractKey, "expiresOn", ev.target.value)}
                            />
                          </div>
                        </div>
                        <div className="mb-2">
                          <label className="form-label">Typ smlouvy</label>
                          {(Object.entries(czechContractTypeNameMap) as [CzechContractType, string][]).map(([type, name]) => {
                            return (
                              <div className="form-check" key={type}>
                                <input
                                  type="radio"
                                  name={`czechContractType-${contractKey}`}
                                  id={`czechContractType-${type}-${contractKey}`}
                                  className="form-check-input"
                                  checked={contract.type === type}
                                  onChange={ev => {
                                    if ((ev.target as HTMLInputElement).checked) {
                                      setContractType(contractKey, type);
                                    }
                                  }}
                                />
                                <label className="form-check-label" htmlFor={`czechContractType-${type}-${contractKey}`}>
                                  {name}
                                </label>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    );
                })}
              </div>
            </div>
          </div>
          <div className="col-6">
            <div className="card">
              <div className="card-header">
                <b>Zaměstnanec</b>
              </div>
              <div className="card-body">
                <div className="mb-2">
                    <label className="form-label">
                      Datum narození
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      value={employeeDateOfBirth}
                      onChange={ev => setEmployeeDateOfBirth(ev.target.value)}
                    />
                </div>
              </div>
            </div>
            <div className="card mt-3">
              <div className="card-header">
                <b>Příplatky</b>
              </div>

              <div className="card-body">
                {paySupplementRules.map((rule: PaySupplementForCalculator, index: number) => {
                    const enabledId = `${rule.type}-enabled`;
                    const appliesToEntireShiftId = `${rule.type}-appliesToEntireShift`;

                    return (
                      <div key={rule.type}>
                        <div className="form-check">
                          <input
                            checked={rule.enabled}
                            className="form-check-input"
                            id={enabledId}
                            onChange={ev => setPaySupplementRuleProperty(rule.type, "enabled", ev.target.checked)}
                            type="checkbox"
                          />
                          <label className="form-check-label" htmlFor={enabledId}>
                            {paySupplementTypeNameMap[rule.type]}
                          </label>
                        </div>

                        <div className="mx-4">
                          {(Object.keys(PaySupplementAmountType) as PaySupplementAmountType[]).map((amountType: PaySupplementAmountType) => {
                            const amountTypeId = `${rule.type}-${amountType}`;
                            return (
                              <div className="form-check" key={amountTypeId}>
                                <input
                                  checked={rule.amountType === amountType}
                                  className="form-check-input"
                                  disabled={!rule.enabled}
                                  id={amountTypeId}
                                  name={amountTypeId}
                                  onChange={ev => ev.target.checked && setPaySupplementRuleProperty(rule.type, "amountType", amountType)}
                                  type="radio"
                                />
                                <label className="form-check-label" htmlFor={amountTypeId}>
                                  {amountType === PaySupplementAmountType.Fixed && "Fixní příplatek"}
                                  {amountType === PaySupplementAmountType.Percentage && "Procentuální příplatek"}
                                </label>
                              </div>
                            );
                          })}

                          <div className="input-group">
                            <input
                              className="form-control text-end"
                              disabled={!rule.enabled}
                              max={rule.amountType === PaySupplementAmountType.Percentage ? 100 : undefined}
                              min={0}
                              onChange={ev => setPaySupplementRuleProperty(rule.type, rule.amountType === PaySupplementAmountType.Percentage ? "percentageIncrease" : "fixedHourlyRate", parseInt(ev.target.value))}
                              type="number"
                              value={rule.amountType === PaySupplementAmountType.Percentage ? rule.percentageIncrease : rule.fixedHourlyRate}
                            />
                            <span className="input-group-text">
                              {rule.amountType === PaySupplementAmountType.Fixed && 'Kč/h'}
                              {rule.amountType === PaySupplementAmountType.Percentage && '%'}
                            </span>
                          </div>

                          <div className="form-check">
                            <input
                              checked={rule.appliesToEntireShift}
                              className="form-check-input"
                              disabled={!rule.enabled}
                              id={appliesToEntireShiftId}
                              onChange={ev => setPaySupplementRuleProperty(rule.type, "appliesToEntireShift", ev.target.checked)}
                              type="checkbox"
                            />
                            <label className="form-check-label" htmlFor={appliesToEntireShiftId}>
                              Příplatek se vztahuje na celou směnu.
                            </label>
                          </div>

                          {index !== paySupplementRules.length - 1 && <hr />}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div className="card mt-3">
          <div className="card-header d-flex justify-content-between align-items-center">
            <b>Dokumenty</b>
          </div>
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center">
              <button onClick={() => addDocument()} className="btn btn-success btn-sm align-right">
                Přidat dokument
              </button>
            </div>

            {documents.length === 0 ? (
              <p className="text-center text-body-secondary">
                Nejsou přidány žádné dokumenty
              </p>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Typ</th>
                    <th>Platí od</th>
                    <th>Platí do</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map(({ key, value }) =>
                    <tr key={key}>
                      <td>
                        <select
                          value={value.type}
                          className="form-select"
                          onChange={ev => setDocumentType(key, (ev.target as HTMLSelectElement).value as CzechDocumentType)}
                        >
                          {Object.values(CzechDocumentType).map(type => <option key={type} value={type}>{documentTypeNameMap[type]}</option>)}
                        </select>
                      </td>
                      <td>
                        <input
                          type="date"
                          className={`form-control ${newMoment(value.startsOn).isAfter(value.expiresOn) ? 'is-invalid' : ''}`}
                          value={newMoment(value.startsOn).format("YYYY-MM-DD")}
                          onChange={ev => setDocumentStartsOn(key, (ev.target as HTMLInputElement).value)}
                        />
                        <div className="invalid-feedback">
                          Platnost končí dříve, než začíná!
                        </div>
                      </td>
                      <td>
                        <input
                          type="date"
                          className={`form-control ${newMoment(value.startsOn).isAfter(value.expiresOn) ? 'is-invalid' : ''}`}
                          value={newMoment(value.expiresOn).format("YYYY-MM-DD")}
                          onChange={ev => setDocumentExpiresOn(key, (ev.target as HTMLInputElement).value)}
                        />
                      </td>
                      <td>
                        <button
                          className="btn-close mt-2 color-red"
                          onClick={() => removeDocument(key)}
                        ></button>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div className="card mt-3">
          <div className="card-header d-flex justify-content-between align-items-center">
            <b>Směny</b>
          </div>
          <div className="card-body">
            {Object.entries(contractGroups).map(([contractKey, { contract, workShifts }]) => {
              const contractStartsOn = newMoment(contract.startsOn);
                  const contractExpiresOn = newMoment(contract.expiresOn);
              return (
                <div key={contractKey}>
                  <div className="d-flex justify-content-between align-items-center">
                    <b>Smlouva #{contractKey.slice(0, 4)} ({czechContractTypeNameMap[contract.type]})</b>
                    <button onClick={() => addWorkShift(contractKey)} className="btn btn-success btn-sm align-right">
                      Přidat směnu
                    </button>
                  </div>

                  {workShifts.length === 0 ? (
                    <p className="text-center text-body-secondary">
                      Nejsou přidány žádné směny
                    </p>
                  ) : (
                    <>
                      <table className="table">
                        <thead>
                          <tr>
                            <th></th>
                            <th><small>Typ</small></th>
                            <th><small>Začátek směny</small></th>
                            <th><small>Konec směny</small></th>
                            <th><small>Hodinová mzda</small></th>
                            <th><small>Přestávka</small></th>
                            <th><small>Začátek přestávky</small></th>
                            <th><small>Délka přestávky</small></th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {workShifts.map(({ key, value }) => {
                            const workShiftPayInterval = getIntervalForWorkShift(value);
                            const workShiftStartTime = moment.tz(value.startTime, timezone);
                            const workShiftFinishTime = moment.tz(value.finishTime, timezone);
                            return (
                              <tr key={key} className={workShiftPayInterval.startsOn !== payInterval.startsOn ? "opacity-50" : ""}>
                                <td className={workShiftPayInterval.startsOn === payInterval.startsOn ? "text-success" : "text-danger"}>
                                  {workShiftPayInterval.startsOn === payInterval.startsOn ? "✓" : "✕"}
                                </td>
                                <td>
                                  <select
                                    value={value.type}
                                    className="form-select"
                                    onChange={ev => setWorkShiftType(contractKey, key, (ev.target as HTMLSelectElement).value as WorkShiftType)}
                                  >
                                    {Object.values(WorkShiftType).map(type => <option key={type} value={type}>{workShiftTypeNameMap[type]}</option>)}
                                  </select>
                                </td>
                                <td>
                                  <input
                                    type="datetime-local"
                                    className={`form-control ${(workShiftStartTime.isAfter(workShiftFinishTime) || workShiftStartTime.isBefore(contractStartsOn) || workShiftStartTime.isAfter(contractExpiresOn)) ? 'is-invalid' : ''}`}
                                    value={workShiftStartTime.format("YYYY-MM-DDTHH:mm")}
                                    onChange={ev => setWorkShiftStartTime(contractKey, key, ev.target.value)}
                                  />
                                  <div className="invalid-feedback">
                                    <div>{workShiftStartTime.isAfter(value.finishTime) && 'Směna končí dříve, než začíná!'}</div>
                                    <div>{workShiftStartTime.isBefore(contractStartsOn) && 'Před začátkem smlouvy!'}</div>
                                    <div>{workShiftStartTime.isAfter(contractExpiresOn) && 'Po konci smlouvy!'}</div>
                                  </div>
                                </td>
                                <td>
                                  <input
                                    type="datetime-local"
                                    className={`form-control ${(workShiftStartTime.isAfter(workShiftFinishTime) || workShiftFinishTime.isBefore(contractStartsOn) || workShiftFinishTime.isAfter(contractExpiresOn)) ? 'is-invalid' : ''}`}
                                    value={workShiftFinishTime.format("YYYY-MM-DDTHH:mm")}
                                    onChange={ev => setWorkShiftFinishTime(contractKey, key, ev.target.value)}
                                  />
                                  <div className="invalid-feedback">
                                    <div>{workShiftStartTime.isAfter(workShiftFinishTime) && 'Směna končí dříve, než začíná!'}</div>
                                    <div>{workShiftFinishTime.isBefore(contractStartsOn) && 'Před začátkem smlouvy!'}</div>
                                    <div>{workShiftFinishTime.isAfter(contractExpiresOn) && 'Po konci smlouvy!'}</div>
                                  </div>
                                </td>
                                <td>
                                  <input
                                    type="number"
                                    className={`form-control text-end ${value.hourlyRate === 0 ? 'is-invalid' : ''}`}
                                    value={value.hourlyRate === 0 ? '' : value.hourlyRate}
                                    min={1}
                                    onChange={ev => setWorkShiftHourlyRate(contractKey, key, parseFloat(ev.target.value))}
                                    placeholder='0'
                                  />
                                  <div className="invalid-feedback">
                                    Nulová mzda!
                                  </div>
                                </td>
                                <td className="text-center">
                                  <div className="justify-content-center">
                                    <input
                                      type="checkbox"
                                      className="form-check-input"
                                      checked={value.workBreaks.length > 0}
                                      onChange={ev => setWorkShiftIncludeBreak(contractKey, key, ev.target.checked)}
                                    />
                                  </div>

                                </td>
                                {(value.workBreaks.length > 0) && (
                                  <>
                                    <td>
                                      <input
                                        type="datetime-local"
                                        className="form-control"
                                        value={newMoment(value.workBreaks[0].startsAt).format("YYYY-MM-DDTHH:mm")}
                                        onChange={ev => setWorkShiftBreakStart(contractKey, key, ev.target.value)}
                                      />
                                    </td>
                                    <td>
                                      <div className="input-group">
                                        <input
                                          type="number"
                                          className="form-control"
                                          value={value.workBreaks[0].durationInMinutes}
                                          min={0}
                                          onChange={ev => setWorkShiftBreakDuration(contractKey, key, parseInt(ev.target.value))}
                                        />
                                        <span className="input-group-text">
                                          min
                                        </span>
                                      </div>
                                    </td>
                                  </>
                                )}
                                {(value.workBreaks.length === 0) && (
                                  <>
                                    <td>
                                      -
                                    </td>
                                    <td>
                                      -
                                    </td>
                                  </>
                                )}

                                <td>
                                  <button
                                    className="btn-close mt-2"
                                    onClick={() => removeWorkShift(contractKey, key)}
                                  ></button>
                                </td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                      <div className="text-muted">
                        <span className="text-success">✓</span> - Zahrnuto do výpočtu
                      </div>
                      <div className="text-muted">
                        <span className="text-danger">✕</span> - Mimo vybrané období pro výpočet
                      </div>
                    </>
                  )}
                </div>
              );
              })}
          </div>
        </div>

        <div className="card mt-3">
          <div className="card-header d-flex justify-content-between align-items-center">
            <b>Externí složky</b>
          </div>
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center">
              <button onClick={() => addExternalComponent()} className="btn btn-success btn-sm align-right">
                Přidat externí složku
              </button>
            </div>

            {externalComponents.length === 0 ? (
              <p className="text-center text-body-secondary">
                Nejsou přidány žádné externí složky
              </p>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Název</th>
                    <th>Typ pracovního poměru</th>
                    <th>Množství</th>
                    <th>Jednotka</th>
                    <th>Přičíst</th>
                    <th>Hrazeno zaměstnavatelem</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {externalComponents.map(({ key, value }) =>
                    <tr key={key}>
                      <td>
                        <input
                          type="text"
                          className="form-control"
                          value={value.name}
                          onChange={ev => setExternalComponentName(key, (ev.target as HTMLInputElement).value)}
                        />
                      </td>
                      <td>
                        <select
                          value={value.salaryType ?? ""}
                          className="form-select"
                          onChange={ev => setExternalComponentSalaryType(key, (ev.target as HTMLSelectElement).value as CzechSalaryType)}
                        >
                          {Object.entries(salaryTypeNameMap).map(([type, name]) => <option key={type} value={type}>{name}</option>)}
                        </select>
                      </td>
                      <td>
                        <input
                          type="number"
                          className="form-control text-end"
                          value={value.amount}
                          onChange={ev => setExternalComponentAmount(key, Number((ev.target as HTMLInputElement).value))}
                        />
                      </td>
                      <td>
                        <select
                          value={value.unit}
                          className="form-select"
                          onChange={ev => setExternalComponentUnit(key, (ev.target as HTMLSelectElement).value as SalaryComponentUnit)}
                        >
                          {Object.entries(salaryComponentUnitNameMap).map(([unit, name]) => <option key={unit} value={unit}>{name}</option>)}
                        </select>
                      </td>
                      <td>
                        <select
                          value={value.calculation ?? ""}
                          className="form-select"
                          onChange={ev => setExternalComponentCalculation(key, (ev.target as HTMLSelectElement).value as SalaryComponentCalculation)}
                        >
                          {Object.entries(salaryComponentCalculationIntentionNameMap).map(([calculation, name]) => <option key={calculation} value={calculation}>{name}</option>)}
                          <option value="">Nikam</option>
                        </select>
                      </td>
                      <td>
                          <input
                            type="checkbox"
                            checked={value.isEmployerCost}
                            onChange={ev => setExternalComponentIsEmployerCost(key, (ev.target as HTMLInputElement).checked)}
                          />
                        </td>
                      <td>
                        <button
                          className="btn-close mt-2 color-red"
                          onClick={() => removeExternalComponent(key)}
                        ></button>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {result.errorMessages.map((errorMessage) => (
          <div className="alert alert-danger mt-3" key={errorMessage}>
            {errorMessage}
          </div>
        ))}

        <div className="mt-3 mb-3">
          <button onClick={recalculate} className="btn btn-primary me-2">Vypočítat</button>
          <button className="btn btn-secondary  me-2" data-bs-toggle="modal" data-bs-target="#exportModal">Export</button>
          <button className="btn btn-secondary" data-bs-toggle="modal" data-bs-target="#importModal">Import</button>
        </div>

        <div className="modal fade" id="exportModal">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Export</h5>
                <button className="btn-close" type="button" data-bs-dismiss="modal" aria-label="Close"></button>
              </div>
              <div className="modal-body">
                <textarea className="form-control" value={getExportString()} id="exportTextarea" readOnly={true} />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">Zavřít</button>
                <button type="button" className="btn btn-primary" onClick={copyExport}>Kopírovat</button>
              </div>
            </div>
          </div>
        </div>

        <div className="modal fade" id="importModal">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Import</h5>
                <button className="btn-close" type="button" data-bs-dismiss="modal" aria-label="Close"></button>
              </div>
              <div className="modal-body">
                <span className="form-label">Vložte datový řetězec:</span>
                <textarea id="importTextarea" className="form-control"></textarea>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">Zavřít</button>
                <button type="button" className="btn btn-secondary" onClick={pasteImport}>Vložit</button>
                <button type="button" className="btn btn-primary" data-bs-dismiss="modal" onClick={submitImport}>Import</button>
              </div>
            </div>
          </div>
        </div>

        {(result.salaries.length > 0 || result.workShifts.length > 0) && <>
          <h3 className="mt-3 mb-3">Výsledky:</h3>
          {result.salaries.map((salary) => {
            const sortedInternalComponents = (Object.entries(salary.components.internal) as [SalaryComponentType, SalaryComponent<SalaryComponentType>][])
              .toSorted(([aType], [bType]) => Object.values(SalaryComponentType).indexOf(aType) - Object.values(SalaryComponentType).indexOf(bType));
            const sortedExternalComponents = (Object.entries(salary.components.external) as [string, SalaryComponent<"external">][])
              .toSorted(([aType, aComp], [bType, bComp]) => Object.values(SalaryComponentCalculation).indexOf(aComp.calculation!) - Object.values(SalaryComponentCalculation).indexOf(bComp.calculation!));
            const sortedComponents: [SalaryComponentType|string, SalaryComponent|null][] = [...sortedInternalComponents];
            for (const [newType, newComp] of sortedExternalComponents) {
                const lastIndexWithCalculation = sortedComponents.findLastIndex(([type, comp]) => comp?.calculation === newComp.calculation);
                if (lastIndexWithCalculation === -1) {
                  sortedComponents.push([newType, newComp]); // todo ideally we would like to "create" the new calculation group in the correct order defined by SalaryComponentCalculation
                  continue;
                }
                sortedComponents.splice(lastIndexWithCalculation + 1, 0, [newType, newComp]);
            }

            return (
              <div key={salary.type}>
                <div className="card mb-3">
                  <h5 className="card-header">Rozpis mzdy {salaryTypeNameMap[salary.type]}</h5>
                  <div className="card-body">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Název a popis</th>
                          <th>Přičteno</th>
                          <th className="text-end">Hodnota</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedComponents.map(([type, component]) => {
                          if (!component) {
                            return;
                          }

                          let amountText = String(component.amount);
                          if (component.unit === SalaryComponentUnit.CZK) {
                            amountText = priceFormat.format(component.amount);
                          }
                          if (component.unit === SalaryComponentUnit.Day) {
                            amountText = `${component.amount}${nbsp}d`;
                          }
                          if (component.unit === SalaryComponentUnit.Hour) {
                            amountText = `${component.amount}${nbsp}h`;
                          }

                          return (
                            <tr key={type}>
                              <td>
                                <p className="my-0">{component.name}</p>
                                <p className="my-0 text-muted">{component.description}</p>
                              </td>
                              <td>
                                {component.calculation ? salaryComponentCalculationIntentionNameMap[component.calculation] : '–'}
                                {component.isEmployerCost && (
                                  <span className="text-muted"> (hrazeno zaměstnavatelem)</span>
                                )}
                              </td>
                              <td className="text-end">
                                <b>{amountText}</b>
                              </td>
                            </tr>
                          )
                        })}
                        <tr key="totalNetSalaryAdvance">
                          <td>
                            <p className="my-0">Peněženka (součet)</p>
                            <p className="my-0 text-muted">Hodnota totalNetSalaryAdvance, která dnes reálně odpovídá součtu v tabulce payment_authorized(in_salary=true).</p>
                          </td>
                          <td>–</td>
                          <td className="text-end">
                            <b>{priceFormat.format(salary.totalNetSalaryAdvance)}</b>
                          </td>
                        </tr>
                        <tr key="unpaidNetSalaryAdvance">
                          <td>
                            <p className="my-0">Peněženka (změnový záznam)</p>
                            <p className="my-0 text-muted">Hodnota unpaidNetSalaryAdvance, která se dnes reálně při změně zapisuje jako nový záznam do tabulky payment_authorized(in_salary=true).</p>
                          </td>
                          <td>–</td>
                          <td className="text-end">
                            <b>{priceFormat.format(salary.unpaidNetSalaryAdvance)}</b>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )
          })}

          {result.workShifts.length > 0 &&
            <div className="card mb-3">
              <h5 className="card-header">Spočítané směny ({result.workShifts.length})</h5>
              <div className="card-body">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>Smlouva</th>
                      <th>Začátek směny</th>
                      <th>Konec směny</th>
                      <th className="text-end">Hodinová mzda</th>
                      <th>Přestávky</th>
                      <th className="text-end">Odpracovaný čas</th>
                      <th className="text-end">Celkový čas</th>
                      <th>Příplatky</th>
                      <th className="text-end">Celková mzda</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.workShifts.map((calculatedWorkShift, index) =>
                      <tr key={calculatedWorkShift.workShift.startTime.toString() + index}>
                        <td>#{calculatedWorkShift.workShift.contractId.slice(0, 4)}</td>
                        <td>{newMoment(calculatedWorkShift.workShift.startTime).format('HH:mm (DD. MM. YYYY)')}</td>
                        <td>{newMoment(calculatedWorkShift.workShift.finishTime).format('HH:mm (DD. MM. YYYY)')}</td>
                        <td className="text-end">{priceFormat.format(calculatedWorkShift.workShift.hourlyRate)}</td>
                        <td>
                          {calculatedWorkShift.workShift.workBreaks.map((workBreak, index) => (
                            <div key={workBreak.startsAt.toString() + index}>
                              {newMoment(workBreak.startsAt).format('HH:mm')}–{newMoment(workBreak.endsAt).format('HH:mm')} ({workBreak.durationInMinutes} minut)
                            </div>
                          ))}
                        </td>
                        <td className="text-end">{displayMinutes(calculatedWorkShift.salary.workTimeTotalNetMinutes)}</td>
                        <td className="text-end">{displayMinutes(calculatedWorkShift.salary.workTimeTotalMinutes)}</td>
                        <td>
                          {calculatedWorkShift.salary.paySupplements.length === 0 && '–'}
                          {calculatedWorkShift.salary.paySupplements.map((supplement, index) => (
                            <div key={supplement.paySupplement.type + index}>

                              {paySupplementTypeNameMap[supplement.paySupplement.type]}:&nbsp;
                              <small>
                                {priceFormat.format(supplement.totalGrossSalary)}
                              </small>
                            </div>
                          ))}
                        </td>
                        <td className="text-end">{priceFormat.format(calculatedWorkShift.salary.totalGrossSalary)}</td>
                        <td>
                          {!calculatedWorkShift.signUp.canSignUp && (
                            <span title={calculatedWorkShift.signUp.issues.map((issue) => issue.message).join(';')}>⚠️</span>
                          )}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          }
        </>}
      </div>
    </div>
  )
}
